import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, Settings2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getConsent,
  acceptAll,
  rejectAll,
  saveConsent,
  CONSENT_OPEN_EVENT,
} from "@/lib/cookieConsent";

const CookieConsent = () => {
  const { t } = useLanguage();
  const c = t.cookieConsent;
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      // small delay to avoid jank during initial paint
      const id = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(id);
    }
  }, []);

  // Allow any "Cookie preferences" trigger across the app to reopen the modal.
  useEffect(() => {
    const handler = () => {
      const existing = getConsent();
      setAnalytics(Boolean(existing?.analytics));
      setMarketing(Boolean(existing?.marketing));
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, handler);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, handler);
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };
  const handleSavePrefs = () => {
    saveConsent(analytics, marketing);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-border/60 bg-background/95 backdrop-blur-md shadow-2xl shadow-primary/10">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="cookie-consent-title"
                className="text-base font-semibold text-foreground"
              >
                {c.title}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                {c.description}{" "}
                <Link
                  to="/legal/cookies"
                  className="text-foreground dark:text-primary underline underline-offset-2 font-semibold"
                >
                  {c.cookiePolicy}
                </Link>
                {" · "}
                <Link
                  to="/legal/privacy"
                  className="text-foreground dark:text-primary underline underline-offset-2 font-semibold"
                >
                  {c.privacyPolicy}
                </Link>
              </p>

              {showSettings && (
                <div className="mt-4 space-y-3 border-t border-border/50 pt-4">
                  <CategoryRow
                    title={c.categories.essential.title}
                    desc={c.categories.essential.desc}
                    checked={true}
                    disabled
                  />
                  <CategoryRow
                    title={c.categories.analytics.title}
                    desc={c.categories.analytics.desc}
                    checked={analytics}
                    onChange={setAnalytics}
                  />
                  <CategoryRow
                    title={c.categories.marketing.title}
                    desc={c.categories.marketing.desc}
                    checked={marketing}
                    onChange={setMarketing}
                  />
                </div>
              )}

              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
                {!showSettings ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="gap-1.5"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    {c.actions.configure}
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleSavePrefs}>
                    {c.actions.save}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleRejectAll}>
                  {c.actions.reject}
                </Button>
                <Button size="sm" onClick={handleAcceptAll}>
                  {c.actions.accept}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CategoryRowProps {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}

const CategoryRow = ({ title, desc, checked, disabled, onChange }: CategoryRowProps) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{desc}</div>
    </div>
    <Switch
      checked={checked}
      disabled={disabled}
      onCheckedChange={(v) => onChange?.(Boolean(v))}
      aria-label={title}
    />
  </div>
);

export default CookieConsent;
